import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  pdf,
} from "@react-pdf/renderer";
import tamilFont from "../assets/fonts/NotoSansTamil-Regular.ttf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

Font.register({
  family: "NotoSansTamil",
  src: tamilFont,
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 35,
    paddingBottom: 30,
    paddingHorizontal: 35,
    fontFamily: "NotoSansTamil",
    fontSize: 11,
  },
  title: {
    fontSize: 24,
    textAlign: "center",
    marginBottom: 10,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 18,
    textAlign: "center",
    marginBottom: 20,
  },
  filterInfo: {
    fontSize: 10,
    marginBottom: 5,
  },
  dateHeading: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 20,
    marginBottom: 5,
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 10,
  },
  row: { flexDirection: "row" },
  headerCell: {
    padding: 5,
    backgroundColor: "#1d70a4dd",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderStyle: "solid",
    fontWeight: "bold",
    textAlign: "center",
    color: "#FFFFFF",
    fontSize: 10,
  },
  cell: {
    padding: 5,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderStyle: "solid",
    textAlign: "center",
    fontSize: 10,
    backgroundColor: "#F5F5F5",
  },
  categoryCell: { width: "25%" },
  descriptionCell: { width: "40%" },
  amountCell: { width: "17.5%" },
});

const TableHeader = () => (
  <View style={styles.row}>
    <Text style={[styles.headerCell, styles.categoryCell]}>Category</Text>
    <Text style={[styles.headerCell, styles.descriptionCell]}>Description</Text>
    <Text style={[styles.headerCell, styles.amountCell]}>Income</Text>
    <Text style={[styles.headerCell, styles.amountCell]}>Expense</Text>
  </View>
);

const ExpensePDFDocument = ({
  filteredExpenses,
  fromDate,
  toDate,
  selectedCategory,
}) => {
  // Group expenses by date
  const groupedByDate = {};
  filteredExpenses.forEach((exp) => {
    const date = exp.date;
    if (!groupedByDate[date]) {
      groupedByDate[date] = [];
    }
    groupedByDate[date].push(exp);
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>Style and Smile</Text>
        <Text style={styles.subtitle}>Expense Report</Text>
        {fromDate && toDate && (
          <Text style={styles.filterInfo}>
            Date Range: {fromDate} to {toDate}
          </Text>
        )}
        {selectedCategory && selectedCategory !== "All Categories" && (
          <Text style={styles.filterInfo}>Category: {selectedCategory}</Text>
        )}
        {Object.keys(groupedByDate).map((date) => {
          const expenses = groupedByDate[date];
          return (
            <View key={date} wrap={false}>
              <Text style={styles.dateHeading}>Date: {date}</Text>
              <View style={styles.table}>
                <TableHeader />
                {expenses.map((exp, idx) => (
                  <View key={idx} style={styles.row} wrap={false}>
                    <Text style={[styles.cell, styles.categoryCell]}>
                      {exp.category_name}
                    </Text>
                    <Text style={[styles.cell, styles.descriptionCell]}>
                      {exp.description || "-"}
                    </Text>
                    <Text style={[styles.cell, styles.amountCell]}>0</Text>
                    <Text style={[styles.cell, styles.amountCell]}>
                      {exp.amount}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          );
        })}
      </Page>
    </Document>
  );
};

export const handleExportPDF = async (
  filteredExpenses,
  fromDate,
  toDate,
  selectedCategory,
) => {
  const blob = await pdf(
    <ExpensePDFDocument
      filteredExpenses={filteredExpenses}
      fromDate={fromDate}
      toDate={toDate}
      selectedCategory={selectedCategory}
    />,
  ).toBlob();
  const pdfUrl = URL.createObjectURL(blob);
  window.open(pdfUrl, "_blank");
};

export const handleExportExcel = (filteredExpenses) => {
  // Group expenses by date
  const groupedByDate = {};
  filteredExpenses.forEach((exp) => {
    const date = exp.date;
    if (!groupedByDate[date]) {
      groupedByDate[date] = [];
    }
    groupedByDate[date].push(exp);
  });

  // Prepare data for Excel
  const tableData = [];
  const merges = [];
  let currentRow = 1; // Header is row 0

  const sortedDates = Object.keys(groupedByDate).sort();

  sortedDates.forEach((date) => {
    const groupSize = groupedByDate[date].length;

    // Add data rows for this date group, with date only in first row
    groupedByDate[date].forEach((exp, idx) => {
      tableData.push({
        Date: idx === 0 ? date : "",
        Category: exp.category_name,
        Description: exp.description || "-",
        Income: "0",
        Expense: exp.amount.toString(),
      });
    });

    // Add vertical merge for Date column in this group
    if (groupSize > 0) {
      merges.push({
        s: { r: currentRow, c: 0 }, // Start of group, Date column
        e: { r: currentRow + groupSize - 1, c: 0 },
      });
    }

    currentRow += groupSize;

    // Add an empty row for spacing
    tableData.push({
      Date: "",
      Category: "",
      Description: "",
      Income: "",
      Expense: "",
    });
    currentRow += 1;
  });

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(tableData, {
    skipHeader: false,
  });

  // Apply merges
  worksheet["!merges"] = merges;

  // Apply styling
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    // Skip header row
    const row = tableData[R - 1];
    if (!row) continue;

    // Style the first row of each group (where Date is not empty) - bold date cell
    if (row.Date && row.Date !== "") {
      const dateCellAddress = XLSX.utils.encode_cell({ r: R, c: 0 });
      if (worksheet[dateCellAddress]) {
        worksheet[dateCellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "E9ECEF" } },
        };
      }
    }
  }

  // Set column widths
  worksheet["!cols"] = [
    { wch: 15 },
    { wch: 20 },
    { wch: 30 },
    { wch: 15 },
    { wch: 15 },
  ];

  // Create workbook and save
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Expense Report");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });
  saveAs(data, `Expense_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
};
